<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import Line from '@/components/common/dashboard/chart/Line.vue'

import { getThisYear, numberFormatter } from "@/js/customChart"
import { weeklyRevenueGrossMarginReport } from '@/api/dashboard';
import { useNavigatorStore } from '@/store/navigator';
const navigatorStore = useNavigatorStore()
import { useUserStore } from '@/store/user'
const userStore = useUserStore()

// props
const props = defineProps({
  chartType: String,
  employeeId: String,
  legend: String,
  width: Number,
  height: Number,
  title: String,
  data: Object
})

// data
const fetchedData = ref()
const mappedData = ref()
const quarter = ref()

defineExpose({
  quarter
})

// compupted
const chartOptions = computed(() => {
  const CHART_OPTIONS = {
    aspectRatio: navigatorStore.isMobile ? 1.2 : 2,
    indexAxis: "x",
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value, index, ticks) {
            return `${value}억`;
          }
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        align: "start",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          generateLabels: (chart) => {
            return [
              {
                text: 'REV',
                fontColor: `#333`,
                fillStyle: "rgba(75, 192, 192, 0.8)",
                lineWidth: 0,
              },
              {
                text: 'GM',
                fontColor: `#333`,
                fillStyle: "rgba(255, 200, 192, 0.8)",
                lineWidth: 0,
              },
            ]
          }
        }
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          labelPointStyle() {
            return {
              pointStyle: false,
            };
          },
          title([data]) {
            return `${data.label} - ${mappedData.value.additionalData.weekdate[data.label]}`
          },
          label(data) {
            return mappedData.value.additionalData.tooltip[data.label]
          }
        }
      }
    }
  }
  return CHART_OPTIONS
})

// watch
watch([() => props.employeeId], async newValue => {
  const [employeeId] = newValue

  try {
    if(props.legend){
      await getWeeklyHistory(employeeId, props.legend)
    }
   
  } catch (err) {
    console.error(err)
  }
})

watch([() => props.legend, () => props.chartType],async newValue => {
  const [legend] = newValue
  if(fetchedData.value){
    mapper(fetchedData.value, legend)
  }else{
    if(legend){
      await getWeeklyHistory(props.employeeId, props.legend)
    await mapper(fetchedData.value, legend)
    }
    
  }
})

// lifecycle
onMounted(async () => {
  const employeeId = userStore.isAdmin ? '' : userStore.user.employeeId
    await getWeeklyHistory(props.employeeId, props.legend)
    quarter.value = ['1','2','3','4']
})

async function mapper(fetchedData, legend) {
  const filteredResult = fetchedData.filter(item => {
    if (legend === 'all') {
      return true
    }
    return item.qvalue.toString() === legend
  })
  const groupByResult = Object.groupBy(filteredResult, ({ title }) => title)
  const weekdateData = groupByResult['REV'].map(item => {
    return [`W${item.itemname}`, `${item.weekdate}.`]
  })
  const tooltipData = Object.entries(Object.groupBy(filteredResult, ({ itemname }) => itemname))
    .map(([key, value]) => {
      function getRevGM(title) {
        return value.find(item => item.title === title)
      }
      const rev = getRevGM('REV')
      const gm = getRevGM('GM')
      const tooltipValue = `Rev. ${numberFormatter(rev.itemvalue)}억원, GM ${numberFormatter(gm.itemvalue)}억원`
      return [`W${key}`, tooltipValue]
    })
  mappedData.value = {
    data: {
      labels: groupByResult['REV'].map(item => {
        return `W${item.itemname}`
      }),
      datasets: [
        {
          data: groupByResult['REV'].map(item => {
            return item.itemvalue
          }),
          backgroundColor: "rgba(75, 192, 192, 0.8)",
          order: 0,
        },
        {
          data: groupByResult['GM'].map(item => {
            return item.itemvalue
          }),
          backgroundColor: "rgba(255, 200, 192, 0.8)",
          order: 1,
          type: props.chartType
        }
      ]
    },
    additionalData: {
      weekdate: {
        ...Object.fromEntries(weekdateData)
      },
      tooltip: {
        ...Object.fromEntries(tooltipData)
      }
    }
  }
}

async function getWeeklyHistory(employeeId, legend) {
  try {
    if(legend){
      const result = await weeklyRevenueGrossMarginReport({
        fs_year: getThisYear(),
        employee_id: employeeId
      })
      fetchedData.value = result.rtn
      mapper(fetchedData.value, legend)
    }
   
  } catch (err) {
    console.error(err)
  }
}

</script>

<template>
  <Line :width="width" :height="height" :data="mappedData?.data" :additionalData="mappedData?.additionalData"
    :title="title" :options="chartOptions" />
</template>